import React, { useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Divider, Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import { useClassifyItem } from "./useClassifyItem";
import ClassificationProgressScreen from "./ClassificationProgressScreen";
import { ClassificationType } from "src/types/classificationTypes";
import "./classification.scss";
import "../landing/landing.scss";
import PillButton from "src/components/UI/PillButton";
import useKeyboardScrollReset from "src/hooks/useKeyboardScrollReset";

interface ManualEntryPageProps {
  onSwitchToScanner: () => void;
}

const ManualEntryPage: React.FC<ManualEntryPageProps> = ({ onSwitchToScanner }) => {
  const [barcode, setBarcode] = useState("");
  const [asin, setAsin] = useState("");
  const { classify, cancel, isAnalyzing, scannedAsin } = useClassifyItem();
  const [scanError, setScanError] = useState<string | null>(null);
  useKeyboardScrollReset();

  const handleClassify = async () => {
    const value = barcode.trim() || asin.trim();
    try {
      setScanError(null);
      await classify(value);
    } catch (err) {
      console.error("[ManualEntry] Classification failed:", err);
      setScanError(
        "We couldn't identify this product. Make sure the barcode entered is correct or try another product.",
      );
    }
  };

  if (isAnalyzing) {
    return (
      <ClassificationProgressScreen
        classificationConfig={{
          type: ClassificationType.SDS,
          asin: scannedAsin,
        }}
        onBack={cancel}
      />
    );
  }

  const canClassify = barcode.trim().length > 0 || asin.trim().length > 0;

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacingInset="400">
      {/* Back arrow */}
      <Row spacingInset="400">
        <Button type="icon" onClick={onSwitchToScanner}>
          <Icon tokens={arrowLeftTokens} />
        </Button>
      </Row>

      {/* Card body */}
      <Column className="home-card" spacing="500" spacingInset="500" alignmentHorizontal="center">
        <Column spacing="400" alignmentHorizontal="center">
          <Text type="h500">Manual Entry</Text>
          <Text color="secondary" type="b300" alignment="center">
            Enter the Barcode or ASIN ID to identify the product and classify
          </Text>
        </Column>

        <Column spacing="500" width="100%">
          <Column spacing="200">
            <Row alignmentVertical="center" spacing="200">
              <Text>Barcode Number</Text>
            </Row>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="Enter 12 or 13 digit barcode"
              value={barcode}
              onChange={(value: string) => setBarcode(value)}
            />
          </Column>

          {/* OR divider */}
          <Row spacing="300">
            <Divider />
            <Text color="secondary">OR</Text>
            <Divider />
          </Row>

          {/* ASIN field */}
          <Column spacing="200">
            <Row alignmentVertical="center" spacing="200">
              <Text>ASIN ID</Text>
            </Row>
            <Input
              type="text"
              placeholder="Enter 10-character ASIN"
              value={asin}
              onChange={(value: string) => setAsin(value)}
            />
          </Column>
        </Column>

        {/* Error message */}
        {scanError && (
          <Text color="error" type="b400">
            {scanError}
          </Text>
        )}

        {/* Classify button */}
        <PillButton text="CLASSIFY" onClick={handleClassify} disabled={!canClassify} />
        <Button type="link" onClick={onSwitchToScanner}>
          SWITCH TO BARCODE SCANNER
        </Button>
      </Column>
    </Column>
  );
};

export default ManualEntryPage;
